import { ReactElement, useEffect, useMemo } from 'react';
import usePaymentRequest from '../../../hooks/payment/usePaymentRequest';
import { loadStripeIfSecure, resolveStripeInit } from '../../../utils/stripe';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import { Elements } from '@stripe/react-stripe-js';
import collect, { Collection } from 'collect.js';
import { PaymentGateway, PaymentMethod } from '../../../services/exports/Constants';
import * as stripeJs from '@stripe/stripe-js';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import { isStripeDirectPaymentMethod } from '../../../utils/payment-options-visibility';

interface Props {
  children?: ReactElement;
}

function Validator({ children }: Props) {
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { setEnabledWallets, setValidatedWallets } = usePaymentFlow();
  const { isMobileApp } = useMobileApp();

  const { initiated, result } = usePaymentRequest({
    options: {
      country: company?.country,
      currency: typeof company?.currency === 'string' ? company?.currency.toLowerCase() : 'eur',
      total: {
        label: `Validation`,
        amount: Number((1 * 100).toFixed(0)),
      },
    },
  });

  useEffect(() => {
    if (initiated && result !== null && !isMobileApp) {
      setEnabledWallets((current) =>
        collect(current)
          .when(
            result?.applePay,
            (collection: Collection<PaymentMethod>) => collection.push(PaymentMethod.ApplePay),
            (collection: Collection<PaymentMethod>) => collection.reject((item) => item === PaymentMethod.ApplePay),
          )
          .toArray(),
      );

      setValidatedWallets((current) => [...current, PaymentMethod.ApplePay]);
    }
  }, [initiated, result, isMobileApp]);

  return children;
}

export default function ApplePayValidator(props: Props) {
  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { getGatewayForMethod } = usePaymentFlow();

  // Asked of the account Apple Pay is charged on: a host registered on the platform but not on the
  // restaurant's account would otherwise show a button whose payment sheet never opens. Only when
  // Stripe takes Apple Pay here: this check also decides the button for a restaurant on Adyen, and a
  // Stripe account left over from before its move holds no hosts and must not hide it.
  const stripeAccount =
    getGatewayForMethod(PaymentMethod.ApplePay) === PaymentGateway.Stripe &&
    isStripeDirectPaymentMethod(company, PaymentMethod.ApplePay)
      ? (resolveStripeInit(company).stripeAccount ?? undefined)
      : undefined;

  const stripePromise = useMemo<null | Promise<stripeJs.Stripe>>(
    () =>
      !company?.service_provider?.stripe_key
        ? null
        : loadStripeIfSecure(company?.service_provider?.stripe_key, stripeAccount ? { stripeAccount } : undefined),
    [company?.service_provider?.stripe_key, stripeAccount],
  );

  return stripePromise ? (
    <Elements key={`apple-pay-${company?.id}-${stripeAccount ?? 'platform'}`} stripe={stripePromise}>
      {company?.id && <Validator key={`apple-pay-status-validation-${company?.id}`}>{children}</Validator>}
    </Elements>
  ) : (
    children
  );
}
